import { getClientFileUrl } from './uploads';
import fallbackYears from '../config/years.json';

let clientsConfigCache = [];
let yearsConfigCache = [];

export const setClientsConfig = (config) => {
  clientsConfigCache = Array.isArray(config) ? config : [];
};

export const getClientsConfig = () => clientsConfigCache;

export const setYearsConfig = (years) => {
  yearsConfigCache = Array.isArray(years) ? years : [];
};

export const getYearsConfig = () => yearsConfigCache;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchJsonFromUploads = async (filePath) => {
  const url = getClientFileUrl(filePath);
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${filePath}: ${response.status}`);
  }
  return response.json();
};

/** Always load clients.json from the uploads API (no local src/config fallback). */
export const fetchClientsConfig = async () => {
  const config = await fetchJsonFromUploads('clients.json');
  if (!Array.isArray(config)) {
    throw new Error('clients.json from uploads is not a valid array');
  }
  return config;
};

export const fetchYearsConfig = async () => {
  try {
    return await fetchJsonFromUploads('years.json');
  } catch {
    return fallbackYears;
  }
};

export const findClientConfig = (companyName) => {
  const config = getClientsConfig();
  if (!config || !companyName) return null;
  const upper = companyName.toUpperCase().trim();

  // Admin-panel aliases → clients.json company / clientId
  const COMPANY_ALIASES = {
    AUTOLINEE: 'GALLO',
    'AUTOLINEE GALLO': 'GALLO',
    GALLO_PALERMO: 'GALLO',
    'AUTOLINEE GALLO PALERMO': 'GALLO',
  };
  const aliased = COMPANY_ALIASES[upper] || upper;

  const exactMatch = config.find((c) => c.company.toUpperCase().trim() === aliased);
  if (exactMatch) return exactMatch;

  const clientIdMatch = config.find((c) => c.clientId?.toUpperCase().trim() === aliased);
  if (clientIdMatch) return clientIdMatch;

  return config.find((c) => {
    const compUpper = c.company.toUpperCase().trim();
    const idUpper = c.clientId?.toUpperCase().trim() || '';
    return aliased.includes(compUpper) || compUpper.includes(aliased)
      || (idUpper && (aliased.includes(idUpper) || idUpper.includes(aliased)));
  });
};

/**
 * Resolve a file reference to its uploads API path.
 *
 * Layout:
 *  - {year}/{clientId}/{file}  → client JSON, logo, PPTX
 *  - lines/{file}              → GTFS line GeoJSON
 *  - zone/{file}               → city zone GeoJSON (project root)
 */
export const resolveUploadPath = (clientConf, fileRef, year) => {
  if (!fileRef) return null;

  if (fileRef.startsWith('zone/') || fileRef.startsWith('lines/')) {
    return fileRef;
  }

  if (fileRef.endsWith('_linee.geojson')) {
    return `lines/${fileRef}`;
  }

  const clientId = clientConf?.clientId || clientConf?.company?.toLowerCase();
  if (clientId && year) {
    const fileName = fileRef.includes('/') ? fileRef.split('/').pop() : fileRef;
    return `${year}/${clientId}/${fileName}`;
  }

  return fileRef;
};

const getLogoCandidatePaths = (clientConf, year) => {
  if (!clientConf?.logoFile) return [];

  const raw = clientConf.logoFile;
  const baseName = raw.split('/').pop();
  const clientId = clientConf.clientId || clientConf.company?.toLowerCase();
  const paths = [];

  if (clientId && year) {
    paths.push(`${year}/${clientId}/${baseName}`);
  }
  if (raw.startsWith('company_logos/')) {
    paths.push(raw);
  }
  if (clientId && year && raw !== baseName) {
    paths.push(`${year}/${clientId}/${raw}`);
  }

  return [...new Set(paths)];
};

export const getClientJsonPath = (clientConf, year) => {
  const plan = getClientCityLoadPlan(clientConf);
  const jsonFile = plan[0]?.jsonFile || clientConf?.jsonFile;
  return resolveUploadPath(clientConf, jsonFile, year);
};

export const getClientLogoPath = (clientConf, year) =>
  resolveUploadPath(clientConf, clientConf?.logoFile, year);

export const getLinesGeojsonPath = (geojsonFile) =>
  geojsonFile ? `lines/${geojsonFile}` : null;

export const getCompanyLogo = (azienda, year) => {
  const clientConf = findClientConfig(azienda);
  if (!clientConf?.logoFile) return null;
  const path = getClientLogoPath(clientConf, year);
  return path ? getClientFileUrl(path) : null;
};

export const getCompanyForCity = (cityName) => {
  if (!cityName) return null;
  const config = getClientsConfig();
  if (!config) return null;
  const upperCity = cityName.toUpperCase().trim();
  for (const c of config) {
    if (c.cities && Object.keys(c.cities).some((city) => city.toUpperCase().trim() === upperCity)) {
      return c.company;
    }
  }
  return null;
};

/** Resolve a city entry from clients.json (case-insensitive) */
export const getCityConfigEntry = (clientConf, cityName) => {
  if (!clientConf?.cities || !cityName) return null;
  const upper = cityName.toUpperCase().trim();
  const cityKey = Object.keys(clientConf.cities).find((c) => c.toUpperCase().trim() === upper);
  if (!cityKey) return null;
  return { cityKey, ...clientConf.cities[cityKey] };
};

/** Per-city load plan: jsonFile, geojsonFile, zoneFile for each configured city */
export const getClientCityLoadPlan = (clientConf) => {
  if (!clientConf) return [];

  if (clientConf.cities && Object.keys(clientConf.cities).length > 0) {
    return Object.entries(clientConf.cities)
      .map(([cityName, cfg]) => ({
        cityName,
        jsonFile: cfg.jsonFile || clientConf.jsonFile || null,
        geojsonFile: cfg.geojsonFile ?? clientConf.geojsonFile ?? null,
        zoneFile: cfg.zoneFile || null,
      }))
      .filter((entry) => entry.jsonFile);
  }

  if (clientConf.jsonFile) {
    return [{
      cityName: null,
      jsonFile: clientConf.jsonFile,
      geojsonFile: clientConf.geojsonFile ?? null,
      zoneFile: null,
    }];
  }

  return [];
};

export const getJsonFileForCity = (companyName, cityName) => {
  const config = findClientConfig(companyName);
  if (!config) return null;
  const cityEntry = getCityConfigEntry(config, cityName);
  if (cityEntry?.jsonFile) return cityEntry.jsonFile;
  return config.jsonFile || null;
};

export const getGeojsonFileForCity = (companyName, cityName) => {
  const config = findClientConfig(companyName);
  if (!config) return null;
  const cityEntry = getCityConfigEntry(config, cityName);
  if (cityEntry?.geojsonFile) return cityEntry.geojsonFile;
  return config.geojsonFile || null;
};

/**
 * Resolve the uploaded PPTX filename for a client/city.
 * When no city is selected and the client has exactly one city, that city's file is used.
 */
export const getPptxFileForCity = (companyName, cityName) => {
  const config = findClientConfig(companyName);
  if (!config) return null;

  if (cityName) {
    const cityEntry = getCityConfigEntry(config, cityName);
    return cityEntry?.pptxFile || null;
  }

  const cityKeys = config.cities ? Object.keys(config.cities) : [];
  if (cityKeys.length === 1) {
    return config.cities[cityKeys[0]]?.pptxFile || null;
  }

  return config.pptxFile || null;
};

export const getPptxUploadPath = (companyName, cityName, year) => {
  const pptxFile = getPptxFileForCity(companyName, cityName);
  if (!pptxFile) return null;
  const clientConf = findClientConfig(companyName);
  return resolveUploadPath(clientConf, pptxFile, year);
};

/** True when clients.json maps a pptxFile for this client/city. */
export const hasPptxConfigured = (companyName, cityName) =>
  Boolean(getPptxFileForCity(companyName, cityName));

/** Check that the PPTX exists on the uploads API (auth required). */
export const checkPptxAvailable = async (companyName, cityName, year) => {
  const path = getPptxUploadPath(companyName, cityName, year);
  if (!path) return false;

  const url = getClientFileUrl(path);
  try {
    const head = await fetch(url, { method: 'HEAD', headers: authHeaders() });
    if (head.ok) return true;
    // Some APIs reject HEAD — fall back to a tiny ranged GET
    if (head.status === 405 || head.status === 501 || head.status === 403) {
      const ranged = await fetch(url, {
        method: 'GET',
        headers: { ...authHeaders(), Range: 'bytes=0-0' },
      });
      return ranged.ok || ranged.status === 206;
    }
    return false;
  } catch {
    return false;
  }
};

/** Download the uploaded PPTX for the current client/city. */
export const downloadClientPptx = async (companyName, cityName, year) => {
  const pptxFile = getPptxFileForCity(companyName, cityName);
  const path = getPptxUploadPath(companyName, cityName, year);
  if (!pptxFile || !path) {
    throw new Error('Nessun PowerPoint configurato per questo cliente/città');
  }

  const url = getClientFileUrl(path);
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(`PowerPoint non disponibile (${response.status})`);
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('PowerPoint vuoto o non disponibile');
  }

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = pptxFile;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const isClientJsonFile = (clientConf, filePath) =>
  getClientCityLoadPlan(clientConf).some((entry) => entry.jsonFile === filePath);

const isClientGeojsonFile = (clientConf, filePath) => {
  if (!filePath || !clientConf) return false;
  if (clientConf.geojsonFile === filePath) return true;
  if (!clientConf.cities) return false;
  return Object.values(clientConf.cities).some((cfg) => cfg.geojsonFile === filePath);
};

/** City names configured for a client in clients.json */
export const getClientCities = (companyName) => {
  const config = findClientConfig(companyName);
  if (!config?.cities) return [];
  return Object.keys(config.cities);
};

/** Match a clients.json city name to the key used in company JSON data */
export const resolveCityDataKey = (cittaObj, cityName) => {
  if (!cityName) return '';
  if (!cittaObj) return cityName;
  if (cittaObj[cityName]) return cityName;
  const upper = cityName.toUpperCase().trim();
  // Try exact uppercase match first (old behavior)
  const exactMatch = Object.keys(cittaObj).find((k) => k.toUpperCase().trim() === upper);
  if (exactMatch) return exactMatch;
  // Fallback: accent-insensitive match for city names with diacritics
  const strip = (s) => s.toUpperCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const stripped = strip(cityName);
  return Object.keys(cittaObj).find((k) => strip(k) === stripped) || cityName;
};

export const getZoneFileForClient = (companyName, cityName) => {
  const config = findClientConfig(companyName);
  const cityEntry = config ? getCityConfigEntry(config, cityName) : null;
  if (cityEntry?.zoneFile) return cityEntry.zoneFile;
  return getZoneFileForCity(cityName);
};

export const getZoneFileForCity = (cityName) => {
  if (!cityName) return null;
  const config = getClientsConfig();
  if (!config) return null;
  const upperCity = cityName.toUpperCase().trim();
  for (const c of config) {
    if (c.cities) {
      const cityKey = Object.keys(c.cities).find((city) => city.toUpperCase().trim() === upperCity);
      if (cityKey) {
        return c.cities[cityKey].zoneFile;
      }
    }
  }
  return null;
};

export const getCurrentCustomerCompany = () => localStorage.getItem('company') || 'ATB';

/**
 * Fetches a JSON/GeoJSON file with bearer token authentication from uploads API.
 * @param {string} companyName - Company display name (e.g. "CTM")
 * @param {string} filePath - File name or relative path from clients.json
 * @param {number} [year] - Required for client-specific files (JSON, logo)
 */
export const fetchCompanyFile = async (companyName, filePath, year) => {
  const clientConf = findClientConfig(companyName);
  let resolvedPath = filePath;

  if (clientConf) {
    if (isClientJsonFile(clientConf, filePath) || filePath === clientConf.logoFile) {
      resolvedPath = resolveUploadPath(clientConf, filePath, year);
    } else if (isClientGeojsonFile(clientConf, filePath)) {
      resolvedPath = getLinesGeojsonPath(filePath);
    } else if (filePath.startsWith('zone/')) {
      resolvedPath = filePath;
    } else if (!filePath.startsWith('lines/')) {
      resolvedPath = resolveUploadPath(clientConf, filePath, year);
    }
  }

  return fetchJsonFromUploads(resolvedPath);
};

export const getCompanyLogoWithAuth = async (azienda, year) => {
  const clientConf = findClientConfig(azienda);
  if (!clientConf?.logoFile) return null;

  const candidates = getLogoCandidatePaths(clientConf, year);
  let lastError = null;

  for (const path of candidates) {
    try {
      const url = getClientFileUrl(path);
      const response = await fetch(url, { headers: authHeaders() });
      if (!response.ok) {
        lastError = new Error(`Failed to fetch logo (${path}): ${response.status}`);
        continue;
      }

      const blob = await response.blob();
      if (!blob.size) {
        lastError = new Error(`Empty logo response (${path})`);
        continue;
      }

      const imageBlob = blob.type.startsWith('image/')
        ? blob
        : new Blob([blob], { type: 'image/png' });
      return URL.createObjectURL(imageBlob);
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error(`Failed to fetch logo for ${azienda}`);
};
